import type { Dispatch, SetStateAction } from "react"
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"

import { Box, ButtonBase, Collapse, Stack, Typography, type ButtonBaseProps } from "@mui/material"
import { alpha } from "@mui/material/styles"
import type { EmblaCarouselType } from "embla-carousel"
import useEmblaCarousel from "embla-carousel-react"
import { noop } from "lodash"

import type { FC, ResponsiveImageType } from ".."
import { Carousel, Container, Grid, Link, ResponsiveImage, SvgIcon, WYSIWYG } from ".."

type TripItinerarySimpleDetail = {
  type: "flight" | "transfer" | "rail" | "flight" | "restaurant" | "carhire"
  extraTypeId?: string
  transferArrival?: string
  transferDeparture?: string
  title: string
}

type TripItineraryPropertyDetail = {
  type: "property"
  propertyTitle: string
  rating: number
  geolocation: { lat: string; lng: string }
  duration: number
  specials?: string
  propertyDescription?: string
  image: ResponsiveImageType
}

type TripItineraryGeneralDetail = {
  type: "extra" | "meal"
  extraTypeId: string
  label: string
  title: string
  name: string
  description1?: string
  description2?: string
  customerDescription?: string
  mealDescription?: string
}

type TripItineraryItem = {
  title: string
  details: (TripItinerarySimpleDetail | TripItineraryPropertyDetail | TripItineraryGeneralDetail)[]
}

type TripItineraryProps = {
  id?: string
  subtitle?: string
  title: string
  items: TripItineraryItem[]
}

// Create a context that will allow us to expand/collapse all itinerary items at once
const TripItineraryContext = createContext<{
  control: [expandAll: boolean | null, setExpandAll: Dispatch<SetStateAction<boolean | null>>]
  numItems: number
}>({ control: [false, noop], numItems: 0 })

const TripItinerary: FC<TripItineraryProps> = ({ id = "itinerary", subtitle, title, items }) => {
  const [expandAll, setExpandAll] = useState<boolean | null>(null)

  return (
    <TripItineraryContext.Provider
      value={{ control: [expandAll, setExpandAll], numItems: items?.length }}
    >
      <Box id={id} py={{ xs: 10, lg: 20 }} bgcolor={(theme) => theme.palette["secondary.200"].main}>
        <Container>
          <TripItineraryHeader title={title} subtitle={subtitle} />
          <TripItineraryControls />
          {items?.map(({ title, details }, i) => (
            <TripItineraryTabItem key={i} {...{ title, details }} index={i + 1} />
          ))}
        </Container>
      </Box>
    </TripItineraryContext.Provider>
  )
}
export default TripItinerary

/** A sub-component for the header content in the trip itinerary component  */
const TripItineraryHeader: FC<Omit<TripItineraryProps, "items">> = ({ subtitle, title }) => (
  <Stack
    alignItems="center"
    rowGap={{ xs: 5, md: 10 }}
    mb={15}
    color={(theme) => theme.palette["secondary.800"].main}
    textAlign="center"
  >
    {subtitle && (
      <Typography variant="heading.md" component="h3" sx={{ fontSize: 32 }}>
        {subtitle}
      </Typography>
    )}
    <Typography variant="heading.sm" component="h2" sx={{ fontSize: 16 }}>
      {title}
    </Typography>
  </Stack>
)

/** A sub-component for the expand/collapse content in the trip itinerary component  */
const TripItineraryControls: FC = () => {
  const {
    control: [_, setExpandAll],
  } = useContext(TripItineraryContext)

  return (
    <Box
      textAlign="center"
      mb={{ xs: 10, md: 40 }}
      sx={{
        "& > *:first-of-type": {
          borderRadius: (theme) => theme.spacing(1, 0, 0, 1),
          borderRightColor: "transparent",
        },

        "& > *:last-of-type": {
          borderRadius: (theme) => theme.spacing(0, 1, 1, 0),
        },
      }}
    >
      <TripItineraryControlBtn
        onClick={() => setExpandAll(true)}
        aria-label="Expand All Itinerary Items"
      >
        Expand All
      </TripItineraryControlBtn>
      <TripItineraryControlBtn
        onClick={() => setExpandAll(false)}
        aria-label="Collapse All Itinerary Items"
      >
        Collapse All
      </TripItineraryControlBtn>
    </Box>
  )
}

/** A sub-component for the control buttons in the trip itinerary component  */
const TripItineraryControlBtn: FC<ButtonBaseProps> = (props) => (
  <ButtonBase
    disableRipple
    sx={(theme) => ({
      ...theme.typography["link.sm"],
      "bgcolor": theme.palette["secondary.200"].main,
      "color": theme.palette["secondary.800"].main,
      "minHeight": { xs: 48, md: 62 },
      "width": "auto",
      "border": `1px solid #C9D2DD`,
      "position": "relative",
      "px": { xs: 7.5, md: 10 },
      "py": 3.5,
      "transition": theme.transitions.create("background-color"),
      "&:hover": {
        bgcolor: theme.palette["neutral.100"].main,
      },
    })}
    {...props}
  />
)

// A context that will allow us to expand/collapse individual itinerary items
const TripItineraryItemContext = createContext<{
  state: [open: boolean, setOpen: Dispatch<SetStateAction<boolean>>]
  index: number
}>({ state: [false, noop], index: 0 })

type TripItineraryTabItemProps = TripItineraryItem & { index: number }

/** A sub-component for each individual tab item in the trip itinerary component  */
const TripItineraryTabItem: FC<TripItineraryTabItemProps> = ({ index, ...props }) => {
  const {
    control: [expandAll, setExpandAll],
  } = useContext(TripItineraryContext)
  const [open, setOpen] = useState(index === 1)

  useEffect(() => {
    if (expandAll !== null) setOpen(expandAll)
    setExpandAll(null)
  }, [expandAll])

  return (
    <TripItineraryItemContext.Provider value={{ state: [open, setOpen], index }}>
      <Grid>
        <TripItineraryTabItemNumbers />
        <TripItineraryTabItemPanel {...props} />
      </Grid>
    </TripItineraryItemContext.Provider>
  )
}

/** A sub-component for the numbers section in the tab item of the trip itinerary component  */
const TripItineraryTabItemNumbers: FC = () => {
  const { numItems } = useContext(TripItineraryContext)

  const {
    state: [open, setOpen],
    index,
  } = useContext(TripItineraryItemContext)

  return (
    <Box
      gridColumn={{ xs: "1 / span 1", lg: "1 / span 2" }}
      display={{ xs: "none", md: "block" }}
      px={2}
      pt={2}
      position="relative"
      sx={{ isolation: "isolate", transition: (theme) => theme.transitions.create("all") }}
    >
      <Box
        width="1px"
        position="absolute"
        top={17}
        bottom={index === numItems ? 0 : -14}
        left="50%"
        sx={(theme) => ({
          background: `linear-gradient(${
            theme.palette[open ? "primary.800" : "secondary.800"].main
          } 25%, ${alpha(theme.palette[open ? "primary.800" : "secondary.800"].main, 0)} 0%)`,
          backgroundSize: "2px 8px",
          backgroundRepeat: "repeat-y",
          transform: "translateX(-50%)",
        })}
      />
      <ButtonBase
        onClick={() => setOpen((open) => !open)}
        sx={{
          aspectRatio: "1",
          bgcolor: (theme) => theme.palette[open ? "primary.800" : "secondary.200"].main,
          borderRadius: 9999,
          color: (theme) => theme.palette[open ? "neutral.100" : "secondary.800"].main,
          display: "flex",
          direction: "row",
          alignItems: "center",
          justifyContent: "center",
          maxWidth: 62,
          mx: "auto",
          position: "relative",
          transition: (theme) => theme.transitions.create("all"),
          width: "100%",
          zIndex: 1,
        }}
        tabIndex={-1}
        aria-label={`${open ? "Expand" : "Collapse"} Itinerary Item ${index}`}
        aria-expanded={open}
        disableRipple
      >
        <Typography variant="heading.sm">{index}</Typography>
      </ButtonBase>
    </Box>
  )
}

/** A sub-component for the panel content section in the tab item of the trip itinerary component  */
const TripItineraryTabItemPanel: FC<TripItineraryItem> = ({ title, details }) => {
  const {
    state: [open],
  } = useContext(TripItineraryItemContext)

  return (
    <Box
      gridColumn={{ xs: "1 / -1", md: "2 / -1", lg: "3 / -1" }}
      bgcolor={(theme) => (open ? theme.palette["neutral.100"].main : "transparent")}
      borderRadius={open ? 1.5 : 0}
      borderTop={`1px solid ${open ? "transparent" : "#DCE3EC"}`}
      boxShadow={(theme) =>
        `1px 1px 2px 0 ${open ? alpha(theme.palette["secondary.800"].main, 0.1) : "transparent"}`
      }
      mr={{ xs: 0, xl: 15 }}
      mb={open ? 5 : "-1px"}
      overflow="hidden"
      sx={{
        "&:last-of-type": {
          borderBottom: "1px solid #DCE3EC",
        },

        "transition": (theme) => theme.transitions.create("all"),
      }}
    >
      <TripItineraryItemTrigger {...{ title }} />

      <Collapse in={open}>
        <Box>
          <TripItineraryItemDetails {...{ details }} />
        </Box>
      </Collapse>
    </Box>
  )
}

/** A sub-component for the panel trigger in the tab item of the trip itinerary component  */
const TripItineraryItemTrigger: FC<Pick<TripItineraryItem, "title">> = ({ title }) => {
  const {
    state: [open, setOpen],
    index,
  } = useContext(TripItineraryItemContext)

  return (
    <ButtonBase
      onClick={() => setOpen((open) => !open)}
      sx={{
        "color": (theme) => theme.palette[open ? "neutral.700" : "secondary.800"].main,
        "display": "flex",
        "direction": "row",
        "alignItems": "center",
        "height": { xs: 72, md: "auto" },
        "justifyContent": { md: "space-between" },
        "px": { xs: 3, md: 15 },
        "py": { xs: 5.5, md: 7 },
        "transition": (theme) => theme.transitions.create("all"),
        "textAlign": "left",
        "width": "100%",

        "&:focus-visible": {
          bgcolor: (theme) => alpha(theme.palette["secondary.800"].main, 0.1),
        },
      }}
      aria-label={`${open ? "Expand" : "Collapse"} Itinerary Item ${index} - ${title}`}
      aria-expanded={open}
      disableRipple
    >
      <Stack
        width={24}
        alignItems="center"
        justifyContent="center"
        bgcolor={(theme) => (open ? theme.palette["primary.800"].main : "transparent")}
        borderRadius={9999}
        mr={2}
        display={{ xs: "flex", md: "none" }}
        flexShrink={0}
        sx={{ width: 28, aspectRatio: "1", transition: (theme) => theme.transitions.create("all") }}
        aria-hidden="true"
      >
        <Typography
          variant="body.md"
          fontWeight={500}
          color={(theme) => theme.palette[open ? "neutral.100" : "secondary.800"].main}
        >
          {index}
        </Typography>
      </Stack>
      <Typography variant="body.lg" component="h2" mr={{ xs: "auto", md: "unset" }}>
        {title}
      </Typography>
      <SvgIcon
        variant="arrow"
        size={{ xs: 12, md: 20 }}
        sx={{
          ml: 5,
          transform: open ? "rotate(90deg)" : "rotate(-90deg)",
          transition: (theme) => theme.transitions.create("all"),
          display: { xs: "none", md: "inline-block" },
        }}
        aria-hidden="true"
      />
    </ButtonBase>
  )
}

/** A sub-component for the panel details in the tab item of the trip itinerary component  */
const TripItineraryItemDetails: FC<Pick<TripItineraryItem, "details">> = ({ details }) => {
  return (
    <Box
      px={{ lg: 15 }}
      pb={{ lg: 5 }}
      borderTop={(theme) => `1px solid ${theme.palette["neutral.200"].main}`}
      sx={{
        "& > * + *": {
          borderTop: (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
        },
      }}
    >
      {details.map((data, i) => {
        switch (data.type) {
          case "property":
            return <TripItineraryItemPropertyDetails {...data} key={i} />
          case "extra":
          case "meal":
            return <TripItineraryItemGeneralDetails {...data} key={i} />
          default:
            return <TripItineraryItemSimpleDetails {...data} key={i} />
        }
      })}
    </Box>
  )
}

/** A sub-component for the property details in the tab item of the trip itinerary component  */
const TripItineraryItemPropertyDetails: FC<TripItineraryPropertyDetail> = ({
  type,
  propertyTitle,
  image,
  geolocation,
  rating,
  duration,
  specials,
  propertyDescription,
}) => {
  const locationLink = `https://www.google.com/maps/search/?api=1&query=${geolocation?.lat},${geolocation?.lng}`
  const stripHtml = (html: string) => html.replace(/<[^>]*>?/gm, "")
  return (
    <Box className="trip-itinerary-detail-item">
      <Stack
        direction="row"
        alignItems="center"
        columnGap={{ xs: 4.5, lg: 10 }}
        px={{ xs: 4.5, lg: 0 }}
        py={{ xs: 4.5, lg: 9.5 }}
      >
        <TripItineraryItemDetailIcon type={type} />
        <Stack rowGap={1}>
          <TripItineraryItemSubtitle label={type} />

          <Stack direction="row" alignItems="center" columnGap={5} flexWrap="wrap">
            <TripItineraryItemTitle label={propertyTitle} />
            <Box display={{ xs: "none", lg: "block" }}>
              <TripItineraryItemRating rating={rating} />
            </Box>
            {geolocation && (
              <Link
                type="base"
                variant="link.xs"
                href={locationLink}
                sx={{
                  "fontSize": 10,
                  "border": (theme) => `1px solid ${theme.palette["neutral.200"].main}`,
                  "fontWeight": 500,
                  "borderRadius": 9999,
                  "px": 3,
                  "py": 2.5,
                  "transition": (theme) =>
                    theme.transitions.create(["background-color", "box-shadow"]),
                  "&:hover": {
                    bgcolor: (theme) => theme.palette["tertiary.100"].main,
                    boxShadow: (theme) =>
                      `1px 1px 2px 0 ${alpha(theme.palette["neutral.900"].main, 0.1)}`,
                  },
                }}
              >
                View On Map
              </Link>
            )}
          </Stack>
        </Stack>
        {duration && (
          <Stack
            flexShrink={0}
            direction="row"
            ml="auto"
            columnGap={3}
            alignItems="center"
            display={{ xs: "none", lg: "flex" }}
          >
            <Stack
              height={48}
              width={48}
              borderRadius={999}
              alignItems="center"
              justifyContent="center"
              bgcolor={(theme) => theme.palette["secondary.200"].main}
              color={(theme) => theme.palette["secondary.800"].main}
            >
              <SvgIcon variant="calendar" size={20} />
            </Stack>
            <Typography variant="body.md" sx={{ b: { fontWeight: 500 } }}>
              <b>{duration}</b> Night Stay
            </Typography>
          </Stack>
        )}
      </Stack>
      {duration && (
        <Stack
          height={54}
          mx={4.5}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          borderTop={(theme) => `1px solid ${theme.palette["neutral.200"].main}`}
          display={{ lg: "none" }}
        >
          <Typography variant="body.md" sx={{ b: { fontWeight: 500 } }} ml={3}>
            <b>{duration}</b> Night Stay
          </Typography>
          <TripItineraryItemRating rating={rating} />
        </Stack>
      )}
      <Stack
        direction={{ lg: "row" }}
        justifyContent="space-between"
        rowGap={10}
        columnGap={22.5}
        mb={{ xs: 4.5, lg: 0 }}
        px={{ xs: 4.5, lg: 0 }}
      >
        <Stack rowGap={5} mb={{ lg: 12 }}>
          {specials && (
            <WYSIWYG
              content={`<p><strong>Meal Plan:</strong> ${specials}</p>`}
              sx={{
                bgcolor: (theme) => theme.palette["primary.200"].main,
                border: (theme) => `1px solid ${theme.palette["primary.300"].main}`,
                px: 3,
                py: 4,
                borderRadius: 1,
              }}
            />
          )}
          {propertyDescription && (
            <WYSIWYG
              content={`<p class='small' style="font-size: 11px">${stripHtml(
                propertyDescription
              )}</p>`}
            />
          )}
        </Stack>
        <Box
          maxHeight={{ lg: 300, md: "auto" }}
          flex={{ lg: "0 0.25 300px", xl: "0 0 300px" }}
          mb={5}
        >
          <ResponsiveImage image={image} borderRadius={2} />
        </Box>
      </Stack>
    </Box>
  )
}

/** A sub-component for the general details in the tab item of the trip itinerary component  */
const TripItineraryItemGeneralDetails: FC<TripItineraryGeneralDetail> = ({
  type,
  extraTypeId,
  title,
  name,
  description1,
  description2,
  mealDescription,
  customerDescription,
}) => {
  const getExtraLabel = () => {
    if (type === "meal") return "Meal"
    switch (extraTypeId) {
      case "1":
        return "Third Party Insurance"
      case "5":
        return "Day Tour"
      case "7":
        return "Multi Day Tour"
      case "59":
        return "Admission"
      case "70":
        return "Coach Transfer"
      case "62":
        return "Gift Certificate"
      case "69":
        return "Guide"
      case "60":
        return "Hotel Pass"
      case "53":
        return "Insurance (Direct)"
      case "63":
        return "Kid's Backpack"
      case "56":
        return "Meet and Greet"
      case "68":
        return "Rail"
      case "61":
        return "Restaurant"
      case "58":
        return "Special Event"
      case "57":
        return "Transit Pass"
      default:
        return "General"
    }
  }
  const getTitle = (): string => {
    if (type === "meal") {
      return mealDescription as string
    }
    return title ?? name
  }

  const iconType = type === "meal" ? "meal" : extraTypeId
  return (
    <Box className="trip-itinerary-detail-item">
      <Stack
        direction="row"
        alignItems="center"
        columnGap={{ xs: 4.5, lg: 10 }}
        px={{ xs: 4.5, lg: 0 }}
        py={{ xs: 4.5, lg: 11 }}
      >
        <TripItineraryItemDetailIcon type={iconType} />
        <Stack rowGap={1}>
          <TripItineraryItemSubtitle label={getExtraLabel()} />
          <TripItineraryItemTitle label={getTitle()} />
        </Stack>
      </Stack>
      {(description1 || description2 || customerDescription) && (
        <Stack rowGap={5} mb={{ xs: 4.5, lg: 12 }} px={{ xs: 4.5, lg: 0 }}>
          {description1 && extraTypeId === "5" && <WYSIWYG content={description1} />}
          {description2 && extraTypeId === "7" && <WYSIWYG content={description2} />}
          {/* Only show customer description if it's not a day tour or multi day tour */}
          {customerDescription && !["5", "7"].includes(extraTypeId as string) && (
            <WYSIWYG content={customerDescription} />
          )}
        </Stack>
      )}
    </Box>
  )
}

/** A sub-component for the simple details in the tab item of the trip itinerary component  */
const TripItineraryItemSimpleDetails: FC<TripItinerarySimpleDetail> = ({
  type,
  title,
  transferArrival,
  transferDeparture,
}) => {
  const isTransfer = type === "transfer"
  const isCarRental = type === "carhire"
  const transferLabel = `${transferDeparture} to ${transferArrival}`
  return (
    <Stack
      className="trip-itinerary-detail-item"
      direction="row"
      alignItems={isCarRental ? "flex-start" : "center"}
      columnGap={{ xs: 4.5, lg: 10 }}
      px={{ xs: 4.5, lg: 0 }}
      py={{ xs: 4.5, lg: 11 }}
    >
      <TripItineraryItemDetailIcon type={type} />
      <Stack rowGap={1}>
        {isCarRental ? (
          <>
            <TripItineraryItemSubtitle label="Car Rental" />
            <Stack>
              <Typography variant="body.md">{`Pick up: ${transferDeparture}`}</Typography>
              <Typography variant="body.md">{`Drop off: ${transferArrival}`}</Typography>
            </Stack>
          </>
        ) : (
          <>
            <TripItineraryItemSubtitle label={type} />
            <TripItineraryItemTitle label={isTransfer ? transferLabel : title} />
          </>
        )}
      </Stack>
    </Stack>
  )
}

/** A sub-component for the detail subtitle in the tab item of the trip itinerary component  */
const TripItineraryItemSubtitle: FC<{ label: string }> = ({ label }) => (
  <Typography variant="body.xs" sx={{ fontWeight: 500, opacity: 0.7, textTransform: "capitalize" }}>
    {label}
  </Typography>
)

/** A sub-component for the detail title in the tab item of the trip itinerary component  */
const TripItineraryItemTitle: FC<{ label: string }> = ({ label }) => (
  <Typography variant="body.lg" component="h2">
    {label}
  </Typography>
)

/** A sub-component for the detail icon in the tab item of the trip itinerary component  */
const TripItineraryItemDetailIcon: FC<{ type: string }> = ({ type }) => {
  const iconProps = {
    size: { xs: 22, lg: 28 },
  }

  const iconComponent = useCallback((type: string) => {
    switch (type) {
      case "property":
        return <SvgIcon variant="bed" {...iconProps} />
      case "transfer":
      case "70":
        return <SvgIcon variant="transfer" {...iconProps} />
      case "68":
        return <SvgIcon variant="train" {...iconProps} />
      case "flight":
        return <SvgIcon variant="plane" {...iconProps} />
      case "61":
      case "meal":
        return <SvgIcon variant="meal" {...iconProps} />
      default:
        return <SvgIcon variant="map" {...iconProps} />
    }
  }, [])

  return (
    <Stack
      flexShrink="0"
      alignItems="center"
      justifyContent="center"
      width={{ xs: 52, lg: 64 }}
      height={{ xs: 52, lg: 64 }}
      borderRadius={2}
      boxShadow={(theme) => `0 0 6px ${alpha(theme.palette["neutral.900"].main, 0.08)}`}
    >
      {iconComponent(type)}
    </Stack>
  )
}

/** A sub-component for the detail carousel in the tab item of the trip itinerary component  */
const TripItineraryItemCarousel: FC<{ images: ResponsiveImageType[] }> = ({ images }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollTrackRef = useRef<HTMLDivElement>(null)
  const scrollThumbRef = useRef<HTMLDivElement>(null)
  const [thumbWidth, setThumbWidth] = useState(20)
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    duration: 60,
    containScroll: "trimSnaps",
    breakpoints: {
      "(min-width: 768px)": {
        dragFree: true,
      },
    },
  })

  const onInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const snaps = emblaApi.scrollSnapList()
      setScrollSnaps(snaps)

      if (containerRef.current && scrollTrackRef.current) {
        const { clientWidth, scrollWidth } = containerRef.current
        const { clientWidth: trackSize } = scrollTrackRef.current
        setThumbWidth(Math.max((clientWidth / scrollWidth) * trackSize, 20))
      }
    },
    [containerRef, scrollTrackRef]
  )

  const onScroll = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!scrollTrackRef.current || !scrollThumbRef.current) return

      const progress = emblaApi.scrollProgress()
      const { clientWidth: trackWidth } = scrollTrackRef.current
      const scrollX = progress * (trackWidth - thumbWidth)
      const thumb = scrollThumbRef.current
      thumb.style.left = `${scrollX}px`
    },
    [thumbWidth]
  )

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const { current: trackCurrent } = scrollTrackRef
      const { current: contentCurrent } = containerRef

      if (!emblaApi || !trackCurrent || !contentCurrent) return

      const { clientX } = e
      const target = e.target as HTMLDivElement
      const rect = target.getBoundingClientRect()
      const trackLeft = rect.left
      const clickRatio = (clientX - trackLeft) / trackCurrent.clientWidth

      const closestSlide = scrollSnaps.reduce((prev, curr) => {
        return Math.abs(curr - clickRatio) < Math.abs(prev - clickRatio) ? curr : prev
      }, 0)
      const closestIndex = scrollSnaps.indexOf(closestSlide)
      emblaApi?.scrollTo(closestIndex)
    },
    [scrollSnaps, scrollTrackRef, containerRef]
  )

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    onSelect(emblaApi)
    emblaApi.on("reInit", onInit)
    emblaApi.on("resize", onInit)
    emblaApi.on("scroll", onScroll)
    emblaApi.on("select", onSelect)
  }, [emblaApi, containerRef, scrollTrackRef, onInit, onScroll])

  return (
    <>
      {images && images.length > 0 && (
        <Box flex={{ lg: "1 0.25 300px", xl: "1 0 300px" }}>
          <Carousel.Root
            carouselRef={emblaRef}
            ref={containerRef}
            viewportProps={{ px: 0 }}
            actions={
              <>
                {!prevBtnDisabled && (
                  <ButtonBase
                    disableRipple
                    sx={{
                      bgcolor: (theme) => theme.palette["neutral.100"].main,
                      borderRadius: 9999,
                      aspectRatio: "1",
                      width: 40,
                      display: { xs: "none", md: "block" },
                      pointerEvents: "all",
                      position: "absolute",
                      left: 20,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                    onClick={() => scrollPrev()}
                  >
                    <SvgIcon variant="arrow" size={12} />
                  </ButtonBase>
                )}
                {!nextBtnDisabled && (
                  <ButtonBase
                    disableRipple
                    sx={{
                      bgcolor: (theme) => theme.palette["neutral.100"].main,
                      borderRadius: 9999,
                      aspectRatio: "1",
                      width: 40,
                      display: { xs: "none", md: "block" },
                      pointerEvents: "all",
                      position: "absolute",
                      right: 20,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                    onClick={() => scrollNext()}
                  >
                    <SvgIcon variant="arrow" size={12} sx={{ rotate: "180deg" }} />
                  </ButtonBase>
                )}
              </>
            }
          >
            {images.map((image, i) => (
              <Carousel.Slide
                key={i}
                borderRadius={1.5}
                flex="0 0 auto"
                minWidth={0}
                mr={3}
                maxWidth={{ xs: 300, lg: "100%" }}
                overflow="hidden"
                sx={{
                  "cursor": "grab",

                  "&:active": {
                    cursor: "grabbing",
                  },

                  "&:last-of-type": {
                    mr: 0,
                  },
                }}
              >
                <ResponsiveImage image={image} />
              </Carousel.Slide>
            ))}
          </Carousel.Root>
          <Box
            width="100%"
            height="2px"
            mt={5}
            position="relative"
            sx={{
              display: { xs: "block", md: "none" },
            }}
          >
            <Box
              ref={scrollTrackRef}
              onClick={(e) => handleTrackClick(e)}
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bgcolor={(theme) => theme.palette["neutral.200"].main}
              sx={{ "&:hover": { cursor: "pointer" } }}
            />
            <Box
              ref={scrollThumbRef}
              position="absolute"
              width={thumbWidth}
              top={0}
              left={0}
              bottom={0}
              bgcolor={(theme) => theme.palette["primary.800"].main}
            />
          </Box>
        </Box>
      )}
    </>
  )
}

/** A sub-component for the detail rating in the tab item of the trip itinerary component  */
const TripItineraryItemRating: FC<Pick<TripItineraryPropertyDetail, "rating">> = ({ rating }) => {
  const fullStars = Math.min(Math.max(Math.floor(rating), 0), 5)
  const halfStar = rating % 1 > 0
  const emptyStars = 5 - fullStars - (halfStar ? 1 : 0)

  return (
    <Stack direction="row" columnGap={1.25}>
      {Array.from({ length: fullStars }).map((_, i) => (
        <SvgIcon
          key={i}
          variant="fullStar"
          size={{ xs: 10, md: 14 }}
          sx={{ fill: (theme) => theme.palette["primary.800"].main }}
        />
      ))}
      {halfStar && (
        <SvgIcon
          variant="halfStar"
          size={{ xs: 10, md: 14 }}
          sx={{ fill: (theme) => theme.palette["primary.800"].main }}
        />
      )}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <SvgIcon
          key={i}
          variant="emptyStar"
          size={{ xs: 10, md: 14 }}
          sx={{ fill: (theme) => theme.palette["primary.800"].main }}
        />
      ))}
    </Stack>
  )
}
